import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Send, Plus, Menu, X, Trash2, MessageSquare, Square } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';

// スマホ/タブレット判定 — チャット入力の送信挙動を分岐するため
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const renderMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
    return `<pre class="code-block"><code>${code.trim()}</code></pre>`;
  });
  html = html.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Headings
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Lists
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>');
  html = html.replace(/<\/ul>\s*<ul>/g, '');

  // Line breaks
  html = html.replace(/\n/g, '<br/>');
  html = html.replace(/<br\/>\s*(<\/?(?:pre|ul|ol|li|h[1-3]))/g, '$1');
  html = html.replace(/(<\/(?:pre|ul|ol|li|h[1-3])>)\s*<br\/>/g, '$1');

  return html;
};

/* ==================== Styled Components ==================== */

const PageContainer = styled.div`
  display: flex;
  /* モバイルの 100vh はアドレスバー裏も含むため入力欄が初期表示で隠れる。
     dvh (動的ビューポート高) でブラウザ UI の表示/非表示に追従させる。 */
  height: calc(100vh - 56px);
  height: calc(100dvh - 56px);
  width: 100%;
  background: ${props => props.theme.colors.bgPrimary};
  overflow: hidden;
  position: relative;
`;

const SidebarOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 40;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => (props.$open ? 'block' : 'none')};
  }
`;

const Sidebar = styled.aside`
  width: 260px;
  min-width: 260px;
  height: 100%;
  background: ${props => props.theme.colors.bgSecondary};
  border-right: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  z-index: 50;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: fixed;
    top: 56px;
    left: 0;
    bottom: 0;
    transform: ${props => (props.$open ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.25s ease;
  }
`;

const SidebarHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NewChatButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }
`;

const SidebarCloseButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const ConversationItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  background: ${props => (props.$active ? props.theme.colors.bgTertiary : 'transparent')};
  border: 1px solid ${props => (props.$active ? props.theme.colors.border : 'transparent')};
  transition: background 0.15s;
  margin-bottom: 2px;

  &:hover {
    background: ${props => props.theme.colors.bgTertiary};
  }
`;

const ConversationInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConversationTitle = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConversationDate = styled.div`
  font-size: 0.65rem;
  color: ${props => props.theme.colors.textMuted};
  margin-top: 2px;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${props => props.theme.colors.textMuted};
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;

  ${ConversationItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: #ef4444;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    opacity: 0.6;
  }
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 100%;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.bgSecondary};
  min-height: 48px;
  flex-shrink: 0;
`;

const HamburgerButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  margin-right: 12px;
  flex-shrink: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

const TopBarTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.mono};
  letter-spacing: 0.5px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${props => (props.$isUser ? 'flex-end' : 'flex-start')};
  padding: 0 4px;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 0 40px;
  }
`;

const MessageBubble = styled(motion.div)`
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.main};
  word-break: break-word;

  ${props =>
    props.$isUser
      ? `
    background: linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.secondary});
    color: white;
    border-bottom-right-radius: 4px;
  `
      : `
    background: ${props.theme.colors.bgTertiary};
    border: 1px solid ${props.theme.colors.border};
    border-bottom-left-radius: 4px;
  `}

  strong { font-weight: 700; }

  h1, h2, h3 {
    margin: 8px 0 4px 0;
    color: inherit;
  }
  h1 { font-size: 1.15rem; }
  h2 { font-size: 1.05rem; }
  h3 { font-size: 0.95rem; }

  ul {
    margin: 4px 0;
    padding-left: 20px;
    list-style: disc;
  }
  li { margin: 2px 0; }

  .code-block {
    background: ${props => props.theme.colors.bgPrimary};
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    padding: 10px;
    margin: 8px 0;
    overflow-x: auto;
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.78rem;
    line-height: 1.5;
  }

  .inline-code {
    background: ${props => props.theme.colors.bgPrimary};
    padding: 2px 5px;
    border-radius: 4px;
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.82rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: 88%;
    font-size: 0.85rem;
  }
`;

const MessageTime = styled.div`
  font-size: 0.6rem;
  color: ${props => props.$isUser ? 'rgba(255,255,255,0.5)' : props.theme.colors.textMuted};
  margin-top: 4px;
  text-align: ${props => (props.$isUser ? 'right' : 'left')};
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${props => props.theme.colors.textMuted};
  padding: 40px 20px 24px;
  text-align: center;
  overflow-y: auto;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(230, 199, 126, 0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const EmptyTitle = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.45rem;
  font-weight: 400;
  font-style: italic;
  color: ${props => props.theme.colors.primary};
  letter-spacing: 0.02em;
  margin-top: 4px;
`;

const EmptyText = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textMuted};
  max-width: 480px;
  line-height: 1.7;
`;

const InputArea = styled.div`
  padding: 10px 12px 14px;
  border-top: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.bgSecondary};
  flex-shrink: 0;

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 10px 56px 14px;
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 8px 12px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.88rem;
  line-height: 1.5;
  resize: none;
  min-height: 24px;
  max-height: 96px;
  padding: 4px 0;

  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const SendButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  background: ${props => {
    if (props.$stop) return props.theme.colors.error || '#D0606A';
    return props.disabled ? props.theme.colors.border : props.theme.colors.primary;
  }};
  border: none;
  border-radius: 8px;
  color: ${props => {
    if (props.$stop) return 'white';
    return props.disabled ? props.theme.colors.textMuted : 'white';
  }};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  background: ${props => props.theme.colors.bgTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  max-width: 70px;
`;

const TypingDot = styled(motion.span)`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${props => props.theme.colors.textMuted};
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ConfirmDialog = styled.div`
  background: ${props => props.theme.colors.bgSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 24px;
  max-width: 340px;
  width: 90%;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ConfirmBtn = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => (props.$danger ? '#ef4444' : 'transparent')};
  color: ${props => props.$danger ? '#fff' : props.theme.colors.textSecondary};

  &:hover {
    background: ${props => (props.$danger ? '#dc2626' : props.theme.colors.bgTertiary)};
  }
`;

const LoginOverlay = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 20px;
  text-align: center;
`;

const LoginPromptTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.fonts.mono};
`;

const LoginPromptDesc = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textMuted};
  max-width: 340px;
  line-height: 1.6;
`;

const LoginPromptButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 28px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.main};
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 8px;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }
`;

const LimitBanner = styled.div`
  padding: 10px 16px;
  background: rgba(239, 68, 68, 0.08);
  border-top: 1px solid rgba(239, 68, 68, 0.2);
  text-align: center;
  font-size: 0.8rem;
  color: #ef4444;
  line-height: 1.5;

  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
    cursor: pointer;
    font-weight: 600;
  }
`;

/* ==================== Component ==================== */

function ChatPage() {
  const { t } = useLanguage();
  const { isAuthenticated, login, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const structCode = searchParams.get('struct_code');
  const promptParam = searchParams.get('prompt');

  // チャットの状態・ストリーミングは ChatProvider が保持 — ページ遷移しても継続する
  const {
    conversations,
    activeConvId,
    setActiveConvId,
    isConvLoading,
    aiUsage,
    sendMessage,
    stopGeneration,
    deleteConversation,
  } = useChat();

  // UI ローカルな状態のみ ChatPage が持つ
  const [inputValue, setInputValue] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [initialSent, setInitialSent] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const activeConv = conversations.find(c => c.id === activeConvId) || null;
  // 表示中の会話が生成中かどうか。他の会話の生成状態には影響されない
  const activeLoading = isConvLoading(activeConvId);
  // 履歴一覧から開いた会話で、本文をサーバーから取得中かどうか
  const loadingHistory = !!(activeConv && activeConv._needsFetch && activeConv.messages.length === 0);
  const limitReached = aiUsage && aiUsage.remaining <= 0;

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConv?.messages, activeLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '24px';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 96) + 'px';
    }
  }, [inputValue]);

  // 結果ページからの初回メッセージ送信 (ChatProvider 側で送信されるため遷移しても継続)
  // forceNew: 必ず新規会話を、選ばれたキャラで開始する。activeConv (前回の別キャラ会話)
  // に追記して別キャラのチャットが続いてしまうのを防ぐ。initialSent が一度きりの保証。
  useEffect(() => {
    if (!initialSent && (promptParam || structCode)) {
      setInitialSent(true);
      const msg = promptParam || `私のSTRUCT CODEは ${structCode} です。この診断結果について教えてください。`;
      sendMessage(msg, { structCode, forceNew: true });
      // 消費済みの URL パラメータを除去 — リロードで初回メッセージが再送信されるのを防ぐ
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptParam, structCode, initialSent]);

  // 送信 — ストリーミング本体は ChatProvider の sendMessage が担う
  const handleSend = useCallback(() => {
    const txt = inputValue.trim();
    if (!txt || activeLoading) return;
    setInputValue('');
    sendMessage(txt);
  }, [inputValue, activeLoading, sendMessage]);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    // IME 変換確定中の Enter は送信扱いにしない (日本語入力での誤送信を防ぐ)
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    // スマホ: キーボードからは送信しない (送信ボタンのみ)。Enter は改行のまま
    if (isMobileDevice()) return;
    // PC: Shift+Enter で送信。Enter 単独は改行 (変換確定の誤送信を回避)
    if (e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // 会話削除 — 削除ダイアログを閉じる + ChatProvider 側で会話を消す
  const handleDeleteConversation = useCallback((id) => {
    deleteConversation(id);
    setDeleteTarget(null);
  }, [deleteConversation]);

  const formatDate = useCallback((iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diff === 0) return t('今日', 'Today');
    if (diff === 1) return t('昨日', 'Yesterday');
    if (diff < 7) return t(`${diff}日前`, `${diff}d ago`);
    return d.toLocaleDateString();
  }, [t]);

  const formatTime = useCallback((iso) => {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  return (
    <PageContainer>
      <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <Sidebar $open={sidebarOpen}>
        <SidebarHeader>
          <NewChatButton onClick={() => {
            setActiveConvId(null);
            setSidebarOpen(false);
          }}>
            <Plus size={16} />
            {t('新しいチャット', 'New Chat')}
          </NewChatButton>
          <SidebarCloseButton onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </SidebarCloseButton>
        </SidebarHeader>

        <ConversationList>
          {conversations.length === 0 && (
            <div style={{ padding: '24px 8px', textAlign: 'center', fontSize: '0.8rem', color: '#555570' }}>
              {t('会話履歴はありません', 'No conversation history')}
            </div>
          )}
          {conversations.map(conv => (
            <ConversationItem
              key={conv.id}
              $active={conv.id === activeConvId}
              onClick={() => { setActiveConvId(conv.id); setSidebarOpen(false); }}
            >
              <MessageSquare size={14} style={{ minWidth: 14, opacity: 0.4 }} />
              <ConversationInfo>
                <ConversationTitle>{conv.title}</ConversationTitle>
                <ConversationDate>{formatDate(conv.created_at)}</ConversationDate>
              </ConversationInfo>
              <DeleteButton onClick={(e) => { e.stopPropagation(); setDeleteTarget(conv.id); }}>
                <Trash2 size={14} />
              </DeleteButton>
            </ConversationItem>
          ))}
        </ConversationList>
      </Sidebar>

      <MainArea>
        <TopBar>
          <HamburgerButton onClick={() => setSidebarOpen(true)}>
            <Menu size={18} />
          </HamburgerButton>
          <TopBarTitle>STRUCT CODE AI</TopBarTitle>
          {isAuthenticated && aiUsage && (
            <div style={{
              marginLeft: 'auto',
              fontSize: '0.75rem',
              color: aiUsage.remaining <= 0 ? '#EF4444' : '#8888a8',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {aiUsage.in_trial
                ? t(`お試し 残り${aiUsage.remaining}/${aiUsage.trial_total}回`, `Trial: ${aiUsage.remaining}/${aiUsage.trial_total} left`)
                : t(`残り ${aiUsage.remaining}/${aiUsage.daily_limit} 回`, `${aiUsage.remaining}/${aiUsage.daily_limit} left`)
              }
              {aiUsage.plan === 'free' && !aiUsage.in_trial && (
                <span style={{ marginLeft: 8, color: '#E6C77E', fontSize: '0.7rem' }}>Free</span>
              )}
              {aiUsage.in_trial && (
                <span style={{ marginLeft: 8, color: '#10B981', fontSize: '0.7rem' }}>Trial</span>
              )}
            </div>
          )}
        </TopBar>

        {!authLoading && !isAuthenticated ? (
          <LoginOverlay>
            <EmptyIcon>
              <MessageSquare size={24} />
            </EmptyIcon>
            <LoginPromptTitle>STRUCT CODE AI</LoginPromptTitle>
            <LoginPromptDesc>
              {t(
                'AIチャットを利用するにはログインが必要です。Googleアカウントで簡単にログインできます。',
                'Please log in to use AI Chat. You can easily sign in with your Google account.'
              )}
            </LoginPromptDesc>
            <LoginPromptButton onClick={login}>
              {t('Googleでログイン', 'Sign in with Google')}
            </LoginPromptButton>
          </LoginOverlay>
        ) : (
          <>
            {activeConv && (activeConv.messages.length > 0 || activeConv._needsFetch) ? (
              <MessagesContainer>
                {activeConv.messages.map((msg, idx) => (
                  <MessageRow key={idx} $isUser={msg.role === 'user'}>
                    <MessageBubble
                      $isUser={msg.role === 'user'}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      {msg.role === 'user' ? (
                        msg.content
                      ) : (
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                      )}
                      <MessageTime $isUser={msg.role === 'user'}>
                        {formatTime(msg.timestamp)}
                      </MessageTime>
                    </MessageBubble>
                  </MessageRow>
                ))}
                {(activeLoading || loadingHistory) && (
                  <MessageRow $isUser={false}>
                    <TypingIndicator>
                      <TypingDot animate={{ y: [0, -5, 0] }} transition={{ duration: 0.5, repeat: Infinity, delay: 0 }} />
                      <TypingDot animate={{ y: [0, -5, 0] }} transition={{ duration: 0.5, repeat: Infinity, delay: 0.12 }} />
                      <TypingDot animate={{ y: [0, -5, 0] }} transition={{ duration: 0.5, repeat: Infinity, delay: 0.24 }} />
                    </TypingIndicator>
                  </MessageRow>
                )}
                <div ref={messagesEndRef} />
              </MessagesContainer>
            ) : (
              <EmptyState>
                <EmptyIcon>
                  <MessageSquare size={24} />
                </EmptyIcon>
                <EmptyTitle>
                  {t('自分の構造について、聞いてみる', 'Ask about your own structure')}
                </EmptyTitle>
                <EmptyText>
                  {t(
                    '5 軸のスコアと天体配置をふまえて、STRUCT CODE が読み解きます。気になっていることを、そのまま書いてください。',
                    'STRUCT CODE reads your five axis scores and natal chart together. Write whatever is on your mind.'
                  )}
                </EmptyText>
              </EmptyState>
            )}

            {limitReached && (
              <LimitBanner>
                {aiUsage?.in_trial === false && aiUsage?.plan === 'free'
                  ? t(
                      '本日のAIチャット回数上限（1回/日）に達しました。明日またご利用いただけます。',
                      'Daily AI chat limit (1/day) reached. You can use it again tomorrow.'
                    )
                  : t(
                      '本日のAIチャット回数上限に達しました。Proプランにアップグレードすると1日30回まで利用できます。',
                      'Daily AI chat limit reached. Upgrade to Pro plan for up to 30 chats per day.'
                    )
                }
              </LimitBanner>
            )}

            <InputArea>
              <InputRow>
                <TextArea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    limitReached
                      ? t('本日の利用回数上限に達しました', 'Daily limit reached')
                      : isMobileDevice()
                        ? t('メッセージを入力...', 'Type a message...')
                        : t('メッセージを入力...（Shift + Enter で送信）', 'Type a message... (Shift + Enter to send)')
                  }
                  rows={1}
                  disabled={activeLoading || limitReached}
                />
                {activeLoading ? (
                  <SendButton
                    type="button"
                    onClick={() => stopGeneration(activeConvId)}
                    $stop
                    whileTap={{ scale: 0.92 }}
                    title={t('生成を停止', 'Stop generating')}
                  >
                    <Square size={14} fill="currentColor" />
                  </SendButton>
                ) : (
                  <SendButton
                    onClick={handleSend}
                    disabled={!inputValue.trim() || limitReached}
                    whileTap={{ scale: 0.92 }}
                  >
                    <Send size={16} />
                  </SendButton>
                )}
              </InputRow>
            </InputArea>
          </>
        )}
      </MainArea>

      {deleteTarget && (
        <ConfirmOverlay onClick={() => setDeleteTarget(null)}>
          <ConfirmDialog onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              {t('会話を削除', 'Delete Conversation')}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#8888a8', lineHeight: 1.5 }}>
              {t('この会話を削除しますか？この操作は元に戻せません。', 'Delete this conversation? This cannot be undone.')}
            </div>
            <ConfirmButtons>
              <ConfirmBtn onClick={() => setDeleteTarget(null)}>
                {t('キャンセル', 'Cancel')}
              </ConfirmBtn>
              <ConfirmBtn $danger onClick={() => handleDeleteConversation(deleteTarget)}>
                {t('削除', 'Delete')}
              </ConfirmBtn>
            </ConfirmButtons>
          </ConfirmDialog>
        </ConfirmOverlay>
      )}
    </PageContainer>
  );
}

export default ChatPage;
